/**
 * Widget dashboard "Follow-up nei prossimi 7 giorni" — cross-project.
 * Mostra reminder pendenti ordinati per data ravvicinata, con click → progetto.
 * Hidden se non ci sono attività imminenti (per non sporcare la dashboard).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Bell, ArrowRight, CalendarClock, Phone, Mail, Users as UsersIcon, MapPin, StickyNote } from 'lucide-react';
import { useFollowUpImminent } from '@/hooks/useAllActivities';
import { useProjectActivities, type ProjectActivityType } from '@/hooks/useProjectActivities';

const TYPE_ICON: Record<ProjectActivityType, React.ReactNode> = {
  call:       <Phone className="h-3.5 w-3.5" />,
  email:      <Mail className="h-3.5 w-3.5" />,
  meeting:    <UsersIcon className="h-3.5 w-3.5" />,
  site_visit: <MapPin className="h-3.5 w-3.5" />,
  note:       <StickyNote className="h-3.5 w-3.5" />,
  reminder:   <Bell className="h-3.5 w-3.5" />,
};

function formatWhen(iso: string | null): { label: string; urgency: 'today' | 'soon' | 'future' } {
  if (!iso) return { label: '—', urgency: 'future' };
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const diffMs = d.getTime() - now.getTime();
  const diffHr = diffMs / (1000 * 60 * 60);
  const diffDay = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (sameDay) {
    return {
      label: `Oggi ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`,
      urgency: 'today',
    };
  }
  if (diffHr < 0) {
    // Già passato (in ritardo)
    return { label: `${Math.abs(diffDay)}g in ritardo`, urgency: 'today' };
  }
  if (diffDay === 1) return { label: 'Domani', urgency: 'soon' };
  if (diffDay <= 3) return { label: `Tra ${diffDay} giorni`, urgency: 'soon' };
  return {
    label: d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' }),
    urgency: 'future',
  };
}

const DashboardFollowUpWidget: React.FC = () => {
  const { data: activities = [], isLoading } = useFollowUpImminent(7);
  const { completeReminder } = useProjectActivities(undefined);
  const navigate = useNavigate();

  if (isLoading || activities.length === 0) return null;

  return (
    <Card className="animate-fade-in border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-amber-600" />
              Follow-up nei prossimi 7 giorni
            </CardTitle>
            <CardDescription>
              {activities.length} {activities.length === 1 ? 'promemoria' : 'promemoria'} cross-progetto · clicca per aprire il progetto
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => navigate('/agenda')}
          >
            Vedi tutta l'agenda
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {activities.slice(0, 6).map(a => {
            const { label: whenLabel, urgency } = formatWhen(a.follow_up_at);
            const urgencyClass = urgency === 'today'
              ? 'text-red-700 bg-red-100 border-red-200'
              : urgency === 'soon'
              ? 'text-amber-800 bg-amber-100 border-amber-200'
              : 'text-slate-700 bg-slate-100 border-slate-200';
            return (
              <div
                key={a.id}
                className="w-full flex items-center gap-3 px-2 py-1.5 rounded hover:bg-white/60 transition-colors group"
              >
                <Checkbox
                  checked={false}
                  onCheckedChange={() => completeReminder(a.id)}
                  className="shrink-0"
                  title="Segna come fatto"
                />
                <button
                  onClick={() => navigate(`/projects/${a.project_id}`)}
                  className="flex-1 min-w-0 text-left flex items-center gap-3"
                >
                  <div className="p-1.5 rounded-full bg-amber-100 text-amber-700 shrink-0">
                    {TYPE_ICON[a.type]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.subject}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {a.project?.name ?? 'Progetto'}
                      {(a.project?.customer_name || a.project?.client) && (
                        <> · {a.project.customer_name ?? a.project.client}</>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`shrink-0 text-[10px] font-medium border ${urgencyClass}`}
                  >
                    {whenLabel}
                  </Badge>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            );
          })}
          {activities.length > 6 && (
            <div className="text-xs text-muted-foreground pt-1 px-2">
              + altri {activities.length - 6} promemoria
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardFollowUpWidget;
